import { useState, useEffect, useRef, useCallback } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Radio, Play, Pause, ExternalLink, Plus, Trash2, Loader2, Volume2, Captions, Eraser, Pencil } from "lucide-react";

const fmtTime = (ts) => { try { return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }); } catch { return ""; } };

export default function AtcRadio() {
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null);   // feed id currently playing
  const [buffering, setBuffering] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);   // feed id being edited (null = adding new)
  const emptyForm = { name: "", airport: "", icao: "", freq: "", description: "", mount: "", direct_url: "", listen_url: "", stream_url: "" };
  const [form, setForm] = useState(emptyForm);
  const [transcribeId, setTranscribeId] = useState(null);  // feed id being transcribed (one at a time)
  const [lines, setLines] = useState([]);                  // {text, ts} newest-first
  const [transcribing, setTranscribing] = useState(false); // request in flight
  const audioRef = useRef(null);
  const transcribeRef = useRef(false);
  const lineIdRef = useRef(0);

  const load = useCallback(async () => {
    try { const r = await api.get("/atc/feeds"); setFeeds(r.data || []); }
    catch { toast.error("Failed to load feeds"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); return () => { transcribeRef.current = false; if (audioRef.current) audioRef.current.pause(); }; }, [load]);

  const stop = () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } setPlaying(null); setBuffering(null); };

  const canPlay = (feed) => !!(feed.mount || feed.direct_url || feed.stream_url);

  const play = (feed) => {
    if (playing === feed.id) { stop(); return; }
    stop();
    if (!canPlay(feed)) { window.open(feed.listen_url, "_blank"); return; }
    setBuffering(feed.id);
    // Try the pasted direct HTML5 URL first (plays straight from the user's browser),
    // then fall back to the same-origin backend proxy.
    const proxyUrl = `${API}/atc/stream/${feed.id}`;
    const direct = (feed.stream_url || feed.direct_url || "").trim();
    const urls = direct ? [direct, proxyUrl] : [proxyUrl];
    let idx = 0;
    const tryNext = () => {
      if (idx >= urls.length) { setBuffering(null); setPlaying(null); toast.error("Feed offline or unreachable right now — try 'LiveATC' or another feed."); return; }
      const url = urls[idx++];
      let advanced = false;
      const fail = () => { if (advanced) return; advanced = true; tryNext(); };
      const a = new Audio(url);
      a.preload = "none";
      a.onplaying = () => { setBuffering(null); setPlaying(feed.id); };
      a.onerror = fail;
      audioRef.current = a;
      a.play().catch(() => fail());
    };
    tryNext();
  };

  const stopTranscribe = () => { transcribeRef.current = false; setTranscribeId(null); setTranscribing(false); };
  const runTranscribeLoop = async (fid) => {
    while (transcribeRef.current) {
      setTranscribing(true);
      try {
        const r = await api.get(`/atc/transcribe/${fid}`, { params: { seconds: 20 }, timeout: 60000 });
        if (!transcribeRef.current) break;
        const t = (r.data?.text || "").trim();
        if (t) setLines((prev) => [{ id: ++lineIdRef.current, text: t, ts: r.data.ts }, ...prev].slice(0, 300));
      } catch (e) {
        if (!transcribeRef.current) break;
        await new Promise((r) => setTimeout(r, 4000));   // brief backoff on error
      } finally { setTranscribing(false); }
    }
  };
  const toggleTranscribe = (feed) => {
    if (transcribeId === feed.id) { stopTranscribe(); return; }
    transcribeRef.current = true;
    setLines([]);
    setTranscribeId(feed.id);
    if (canPlay(feed) && playing !== feed.id) play(feed);   // also start audio so the user hears it
    runTranscribeLoop(feed.id);
  };
  const clearLines = () => setLines([]);

  const openAdd = () => { setEditId(null); setForm(emptyForm); setShowAdd(true); };
  const openEdit = (f) => {
    setEditId(f.id);
    setForm({ ...emptyForm, ...Object.fromEntries(Object.keys(emptyForm).map((k) => [k, f[k] ?? ""])) });
    setShowAdd(true);
  };
  const saveFeed = async () => {
    if (!form.name.trim() || (!form.mount.trim() && !form.stream_url.trim() && !form.listen_url.trim())) { toast.error("Enter a name and a LiveATC mount, stream URL or LiveATC page URL"); return; }
    try {
      if (editId) await api.put(`/atc/feeds/${editId}`, form);
      else await api.post("/atc/feeds", form);
      toast.success(editId ? "Feed updated" : "Feed added");
      setShowAdd(false); setEditId(null); setForm(emptyForm); load();
    } catch { toast.error("Save failed"); }
  };
  const removeFeed = async (id) => { try { await api.delete(`/atc/feeds/${id}`); setFeeds((f) => f.filter((x) => x.id !== id)); if (playing === id) stop(); } catch { toast.error("Delete failed"); } };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (loading) return <div className="py-16 text-center text-slate-400" data-testid="atc-loading"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;

  return (
    <div className="space-y-4" data-testid="atc-radio">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-slate-500 max-w-2xl">
          Live air-traffic-control audio from <span className="font-medium text-slate-700">LiveATC</span> volunteer feeds, streamed in-app. Press play to listen — availability varies by feed and time of day. Use "LiveATC" to open the source page in a new tab.
        </p>
        <Button size="sm" data-testid="add-feed-btn" onClick={() => (showAdd ? setShowAdd(false) : openAdd())} className="bg-slate-900 hover:bg-slate-800 text-white shrink-0">
          <Plus className="h-4 w-4 mr-1.5" /> {showAdd ? "Close" : "Add feed"}
        </Button>
      </div>

      {showAdd && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 grid sm:grid-cols-2 gap-3" data-testid="add-feed-form">
          <div className="sm:col-span-2 text-xs text-slate-400 -mb-1">On <span className="font-medium text-slate-600">liveatc.net/search/?icao=YSBK</span> each feed (ATIS, Tower, R405…) has an <span className="font-medium text-slate-600">HTML5 player</span> — copy its audio URL (e.g. <span className="font-mono text-slate-600">https://d.liveatc.net/ysbk_twr</span>) and paste it below.</div>
          <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Name *</Label><Input data-testid="feed-name" className="mt-1.5" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Bankstown Tower" /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Airport</Label><Input className="mt-1.5" value={form.airport} onChange={(e) => set("airport", e.target.value)} /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">ICAO</Label><Input className="mt-1.5" value={form.icao} onChange={(e) => set("icao", e.target.value)} placeholder="YSBK" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Freq</Label><Input className="mt-1.5" value={form.freq} onChange={(e) => set("freq", e.target.value)} /></div>
          </div>
          <div className="sm:col-span-2"><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Direct HTML5 stream URL <span className="normal-case font-normal text-slate-400">(from the LiveATC feed)</span></Label><Input data-testid="feed-stream" className="mt-1.5 font-mono text-xs" value={form.stream_url} onChange={(e) => set("stream_url", e.target.value)} placeholder="https://d.liveatc.net/ysbk_twr" /></div>
          <div className="sm:col-span-2"><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">…or a LiveATC mount name <span className="normal-case font-normal text-slate-400">(alternative)</span></Label><Input data-testid="feed-mount" className="mt-1.5" value={form.mount} onChange={(e) => set("mount", e.target.value)} placeholder="e.g. ysbk_twr" /></div>
          <div className="sm:col-span-2"><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">LiveATC player page (fallback link)</Label><Input className="mt-1.5" value={form.listen_url} onChange={(e) => set("listen_url", e.target.value)} placeholder="https://www.liveatc.net/hlisten.php?mount=…" /></div>
          <div className="sm:col-span-2 flex justify-end"><Button data-testid="save-feed-btn" onClick={saveFeed} className="bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold">{editId ? "Update feed" : "Save feed"}</Button></div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="atc-feeds">
        {feeds.length === 0 && <p className="text-sm text-slate-400">No feeds yet. Add one with a LiveATC stream URL.</p>}
        {feeds.map((f) => (
          <div key={f.id} className={`bg-white border rounded-xl p-4 transition-colors ${playing === f.id ? "border-lime-400 ring-1 ring-lime-200" : "border-slate-200"}`} data-testid={`feed-${f.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-heading font-semibold text-slate-900 truncate flex items-center gap-1.5">
                  {playing === f.id ? <Volume2 className="h-4 w-4 text-lime-500 animate-pulse" /> : <Radio className="h-4 w-4 text-slate-400" />}
                  {f.name}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">{[f.icao, f.freq].filter(Boolean).join(" · ")}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button className="text-slate-300 hover:text-slate-900" data-testid={`edit-feed-${f.id}`} onClick={() => openEdit(f)}><Pencil className="h-4 w-4" /></button>
                <button className="text-slate-300 hover:text-red-600" data-testid={`delete-feed-${f.id}`} onClick={() => removeFeed(f.id)}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            {f.description && <p className="text-xs text-slate-400 mt-2 line-clamp-2">{f.description}</p>}
            <div className="flex items-center gap-2 mt-3">
              <Button size="sm" data-testid={`play-feed-${f.id}`} onClick={() => play(f)}
                className={playing === f.id ? "bg-lime-500 hover:bg-lime-400 text-slate-900" : "bg-slate-900 hover:bg-slate-800 text-white"}>
                {buffering === f.id ? <Loader2 className="h-4 w-4 animate-spin" /> : playing === f.id ? <><Pause className="h-4 w-4 mr-1" /> Stop</> : <><Play className="h-4 w-4 mr-1" /> {canPlay(f) ? "Play" : "Listen"}</>}
              </Button>
              {canPlay(f) && (
                <Button size="sm" variant={transcribeId === f.id ? "default" : "outline"} data-testid={`transcribe-feed-${f.id}`}
                  onClick={() => toggleTranscribe(f)}
                  className={transcribeId === f.id ? "bg-red-500 hover:bg-red-400 text-white" : "border-slate-200"}>
                  {transcribeId === f.id && transcribing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Captions className="h-4 w-4 mr-1" />}
                  {transcribeId === f.id ? "Stop" : "Transcribe"}
                </Button>
              )}
              {f.listen_url && <a href={f.listen_url} target="_blank" rel="noreferrer" data-testid={`open-feed-${f.id}`} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"><ExternalLink className="h-3.5 w-3.5" /> LiveATC</a>}
            </div>
            {transcribeId === f.id && (
              <div className="mt-3 border-t border-slate-100 pt-2.5" data-testid={`transcript-${f.id}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                    {transcribing ? <Loader2 className="h-3 w-3 animate-spin text-red-500" /> : <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse inline-block" />}
                    Live transcript
                  </span>
                  <button onClick={clearLines} data-testid={`clear-transcript-${f.id}`} className="text-[11px] text-slate-400 hover:text-slate-900 inline-flex items-center gap-1"><Eraser className="h-3 w-3" /> Clear</button>
                </div>
                <div className="max-h-44 overflow-y-auto space-y-1 pr-1" data-testid={`transcript-lines-${f.id}`}>
                  {lines.length === 0
                    ? <p className="text-xs text-slate-400 italic">Listening… lines appear here (newest first). ATC audio is noisy — expect errors.</p>
                    : lines.map((l) => (
                        <p key={l.id} className="text-xs text-slate-700 leading-snug"><span className="text-slate-300 mr-1.5 tabular-nums">{fmtTime(l.ts)}</span>{l.text}</p>
                      ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
